import React, {
    useState,
    useCallback,
    useMemo,
    useRef,
} from "react";
import _ from "lodash";
import moment from "moment";
import TableComponent from "components/Table";
import PageWrapper from "components/wrappers/PageWrapper";
import { useTranslation } from "react-i18next";
import DataTrainingService from "api/services/DataTrainingService";
import AssignIntent from "./AssignIntent";
import OnDeleteConfirm from "components/modals/OnDeleteConfirm";
import { t } from "i18next";
import ToastAlert from "components/ToastAlert";
import PropTypes from 'prop-types';
import { ExportCSV } from "components/ExportCSV";

const baseURL = process.env.REACT_APP_API_HOST_HTTPS;

function IntentIdCell_() {
    return <span>#</span>;
}
function IntentMessageCell_({ row }) {
    return <div className="w-96">{row.values.message}</div>;
}
function IntentLanguageCell_({ row }) {
    return <div className="text-sm">{row.values.language}</div>;
}
function IntentTimeCell({ value }) {
    return (
        <span className="text-xs rounded px-2 py-1 text-gray-600 bg-gray-100">
            {moment(value).local().format("DD MMM YYYY - HH:mm")}
        </span>
    );
}
function IntentActionsCell_({ data, row, column }) {
    const id = data[row.id]?._id;
    return (
        <div className="flex justify-end">
            <AssignIntent onDataSubmit={column.onAssignIntent} message={row.values} />
            <div className="flex items-center mx-2">
                <OnDeleteConfirm onConfirm={() => column.onDeleteIntent(id)} key={"d3-1"} />
            </div>
        </div>
    );
}

IntentMessageCell_.propTypes = {
    row: PropTypes.shape({ values: PropTypes.shape({ message: PropTypes.string }) }),
};
IntentLanguageCell_.propTypes = {
    row: PropTypes.shape({ values: PropTypes.shape({ language: PropTypes.string }) }),
};
IntentTimeCell.propTypes = {
    value: PropTypes.any,
};
IntentActionsCell_.propTypes = {
    data: PropTypes.array,
    row: PropTypes.shape({ id: PropTypes.string, values: PropTypes.object }),
    column: PropTypes.shape({ onAssignIntent: PropTypes.func, onDeleteIntent: PropTypes.func }),
};

function IntentList_() {
    const { t } = useTranslation();
    const [pageCount, setPageCount] = useState(0);
    const [data, setData] = useState([]);
    const fetchIdRef = useRef(0);
    const [loading, setLoading] = useState(false);
    const [successIntentAssign, setSuccessIntentAssign] = useState(null)
    const [successDeleteMsg, setSuccessDeleteMsg] = useState(null)
    const [searchTerm, setSearchTerm] = useState("");

    const fetchAPIData = async ({ limit, skip, search }) => {
        try {
            setLoading(true);
            const response = await DataTrainingService.getNuMessageListWithPagination(limit, skip, search)
            setData(response.data);
            setPageCount(response.paging.pages);
            setLoading(false);
        } catch (e) {
            setLoading(false);
            console.error(e);
        }
    };

    const fetchData = useCallback(
        ({ pageSize, pageIndex }) => {
            const fetchId = ++fetchIdRef.current;
            setLoading(true);
            if (fetchId === fetchIdRef.current) {
                fetchAPIData({
                    limit: pageSize,
                    skip: pageSize * pageIndex,
                    search: searchTerm,
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchTerm, successDeleteMsg, successIntentAssign]
    );

    const _handleSearch = _.debounce(
        (search) => {
            setSearchTerm(search);
        },
        1500,
        {
            maxWait: 1500,
        }
    );

    function resetActionStates() {
        setSuccessDeleteMsg(null)
        setSuccessIntentAssign(null)
    }

    // APIs Calls
    const onAssignIntent = (id, data) => {

        if (id && data) {
            resetActionStates()
            DataTrainingService.assignIntent(id, data).then((resp) => {
                setSuccessIntentAssign(true)
            }).catch((err) => {
                setSuccessIntentAssign(false)
            });
        }

    }

    const onDeleteIntent = (id) => {
        if (id) {
            resetActionStates()
            DataTrainingService.deleteIntent(id).then((resp) => {
                setSuccessDeleteMsg(true)
            }).catch((err) => {
                setSuccessDeleteMsg(false)
            });
        }

    }

    const columns = useMemo(() => [
        {
            Header: () => "#",
            accessor: '_id',
            id: '_id', // 'id' is required,
            show: false,
            Cell: IntentIdCell_,
        },
        {
            Header: t('data_training.message_not_understood'),
            accessor: "message",
            Cell: IntentMessageCell_,
        },
        {
            Header: t('form.language'),
            accessor: "language",
            Cell: IntentLanguageCell_,
        },
        {
            Header: t('form.time'),
            accessor: "createAt",
            Cell: IntentTimeCell,
            show: true,
        },
        {
            Header: "",
            accessor: "actions",
            onAssignIntent,
            onDeleteIntent,
            Cell: IntentActionsCell_,
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t]);


    return (
        <PageWrapper
            title={t('data_training.message_not_understood')}
            actions={
            <ExportCSV csvData={data}
            disabled={!data} 
            fileName={`messages_nu_${new Date().toISOString().split('T')[0]}`} />
            }
            className="border"
        >
            <div className="container mx-auto flex flex-col">
                <div className="mt-3">
                    <input
                        id="searchTerm"
                        name="searchTerm"
                        type="text"
                        onChange={(e) => _handleSearch(e.target.value)}
                        placeholder="Search by message"
                        className="appearance-none block w-1/4 float-right px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                </div>
                <div className="flex justify-center">
                    <TableComponent
                        pageCount={pageCount}
                        fetchData={fetchData}
                        columns={columns}
                        loading={loading}
                        data={data}
                    />
                </div>
                <ActionsAlert successDeleteMsg={successDeleteMsg} successIntentAssign={successIntentAssign} />
            </div>

        </PageWrapper>




    );
}

export default IntentList_;


function ActionsAlert({ successIntentAssign, successDeleteMsg }) {
    return (
        <>
            {successIntentAssign === true && <ToastAlert message={t('alert.success_operation')} type={'success'} timeOut={5000} />}
            {successIntentAssign === false && <ToastAlert message={t('alert.failed_operation')} type={'error'} timeOut={5000} />}
            {successDeleteMsg === true && <ToastAlert message={t('alert.success_operation')} type={'success'} timeOut={5000} />}
            {successDeleteMsg === false && <ToastAlert message={t('alert.failed_operation')} type={'error'} timeOut={5000} />}
        </>
    )
}


function ExportData({ disabled }) {
    return <a href={`${baseURL}/data-training/messages_nu/export`} target="_blank" rel="noopener noreferrer">
        <button disabled={!!disabled}
            className={`flex items-center justify-center shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2  transition duration-150 ease-in-out 
  rounded  px-4 py-2 text-sm ${disabled ? 'bg-gray-200 text-gray-400' : 'text-white bg-green-600 focus:ring-green-700 hover:bg-green-600'}`}
        >
            <svg className="w-4 h-4 pr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg> Export

        </button>
    </a>
}
ActionsAlert.propTypes = {
  successIntentAssign: PropTypes.bool,
  successDeleteMsg: PropTypes.bool,
};

ExportData.propTypes = {
  disabled: PropTypes.bool,
};
